"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FAQS = [
  {
    q: "What is 8Dfy?",
    a: "A free browser extension that transforms YouTube, YouTube Music, Spotify Web, and Apple Music into immersive 8D spatial sound in real-time. It uses the Web Audio API to process audio entirely in your browser \u2014 zero data sent anywhere.",
  },
  {
    q: "Which music players are supported?",
    a: "8Dfy works on YouTube, YouTube Music, and Apple Music in Chrome and Firefox. Spotify Web is supported in Chrome 116+: start a song, open 8Dfy from the toolbar, and turn Spatial Audio on once to connect that tab.",
  },
  {
    q: "What does Auto DJ do?",
    a: "On YouTube and compatible media players, Auto DJ waits at least 60 seconds, listens for a strong beat, then carries 4\u20138 beats from the outgoing song over the beginning of the next playlist track. It crossfades the two and can make a small temporary tempo adjustment so the change feels mixed instead of abruptly switched. Spotify currently supports spatial processing, while Auto DJ there is still in progress.",
  },
  {
    q: "Do I need headphones?",
    a: "Yes. 8D rotation, multi-band surround, HRTF 3D, and binaural beats all rely on sending different signals to each ear. Any headphones or earbuds will work.",
  },
  {
    q: "Is it really free?",
    a: "The core experience is 100% free forever \u2014 8D rotation on YouTube, 8 presets with sub-variants, bass/clarity/reverb controls, the real-time visualizer, 30-second shareable video samples, plus a 60-second preview of every Pro mode. Pro ($4.99 one-time) makes Surround, HRTF 3D, and Binaural Beats unlimited and unlocks full-length exports (up to 10 minutes as 1080p MP4 video, lossless WAV, or M4A audio).",
  },
  {
    q: "Does 8Dfy collect my data?",
    a: "No. Everything runs client-side in your browser. No analytics, no tracking, no account required. Your audio never leaves your device.",
  },
  {
    q: "Can I save or share my 8D mixes?",
    a: "Yes. Everyone can export a 30-second 1080p video clip with a live audio-reactive visualizer \u2014 perfect for Shorts, TikTok, or sending to a friend. Pro unlocks full-length recordings up to 10 minutes, plus lossless WAV and M4A audio formats.",
  },
  {
    q: "How do I get a Pro license key?",
    a: "Click \u201cGet Pro License\u201d, pay $4.99 via Gumroad, and you\u2019ll instantly receive a license key by email. Paste it into the extension when prompted. One-time payment, lifetime access.",
  },
];

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;
  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      {/* h3 wrapper: FAQ questions become reachable via screen-reader heading nav */}
      <h3 className="m-0">
        <button
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between py-5 text-left group hover:cursor-pointer"
        >
          <span className="text-sm font-bold text-[#b3b3b3] group-hover:text-white transition-colors duration-200 pr-4">
            {q}
          </span>
          <span
            aria-hidden="true"
            className={`text-[#b3b3b3]/75 text-xl shrink-0 transition-transform duration-300 ${open ? "rotate-45" : ""}`}
          >
            +
          </span>
        </button>
      </h3>
      {/* max-h-96 (was max-h-40): long answers were getting clipped on narrow screens */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-5" : "max-h-0"}`}
      >
        <p className="text-sm text-[#b3b3b3] leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-faq]", {
        scrollTrigger: { trigger: ref.current, start: "top 80%", toggleActions: "play none none none" },
        y: 24,
        autoAlpha: 0,
        duration: 0.5,
        ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="faq" ref={ref} className="section">
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#2EC4B6] mb-3">
        FAQ
      </p>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
        Got questions?
      </h2>

      <div data-faq className="mt-10">
        {FAQS.map((faq, i) => (
          <FAQItem key={faq.q} {...faq} index={i} />
        ))}
      </div>

      {/* Exit path: someone who read the FAQ is warm — don't dead-end them */}
      <p className="mt-8 text-sm text-[#b3b3b3]">
        Still curious?{" "}
        <a href="#pricing" title="See 8Dfy Pro features and pricing" className="link-underline inline-block font-bold text-[#FF3366] hover:text-[#FF6B8A] transition-colors duration-200">
          See what Pro unlocks
        </a>{" "}
        or{" "}
        <a href="mailto:hello@8dfy.xyz" title="Email 8Dfy support" className="link-underline inline-block font-bold text-[#2EC4B6] hover:text-white transition-colors duration-200">
          ask us anything
        </a>
        .
      </p>
    </section>
  );
}
